import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/ventrix/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StatusChip, ModeIcon, formatDate, formatDateShort } from "@/components/ventrix/atoms";
import TrackingTimeline from "@/components/ventrix/TrackingTimeline";
import TrackingMap from "@/components/ventrix/TrackingMap";
import { Search, MapPin, ArrowRight, Printer } from "lucide-react";

export default function Track() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("numbers") || "";
  const [numbers, setNumbers] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);

  const run = async (raw) => {
    if (!raw.trim()) return;
    setLoading(true); setError(""); setResults(null);
    try {
      const { data } = await api.get(`/v1/track`, { params: { numbers: raw } });
      setResults(data.results);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (initial) run(initial); }, []); // eslint-disable-line

  const submit = (e) => {
    e.preventDefault();
    setParams({ numbers: numbers.trim() });
    run(numbers);
  };

  return (
    <div className="min-h-screen">
      <PublicHeader />
      <section className="bg-[#0E2F76] text-white">
        <div className="vtx-container py-12">
          <div className="vtx-eyebrow text-[#A9C0E0]">/ Live tracking</div>
          <h1 className="mt-3 font-black text-3xl md:text-5xl tracking-tighter">Where's my cargo?</h1>
          <form onSubmit={submit} className="mt-8 flex flex-col md:flex-row gap-3 max-w-3xl">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0E2F76]" />
              <input
                data-testid="track-input"
                value={numbers}
                onChange={(e)=>setNumbers(e.target.value)}
                placeholder="One or more VTX numbers, comma-separated"
                className="vtx-input pl-10 font-mono tracking-wider"
              />
            </div>
            <button type="submit" className="vtx-btn-danger inline-flex items-center gap-2 justify-center" data-testid="track-submit">
              Track <ArrowRight size={16}/>
            </button>
          </form>
        </div>
      </section>

      <section className="vtx-container py-12">
        {loading && <SkeletonResults />}
        {error && <div className="vtx-card p-6 border-l-4 border-l-[#d41f26]"><div className="text-[#d41f26] font-semibold">{error}</div></div>}
        {results && !loading && (
          <div className="space-y-8">
            {Object.entries(results).map(([num, ship]) => (
              <ResultCard key={num} num={num} ship={ship} />
            ))}
          </div>
        )}
        {!loading && !results && !error && (
          <div className="vtx-card p-10 text-center">
            <MapPin size={32} className="mx-auto text-[#A9C0E0]" />
            <div className="mt-3 font-bold text-[#0E2F76]">Enter one or more tracking numbers to begin.</div>
            <div className="text-sm text-[#475569] mt-1">Every VTX number is unguessable and rate-limited against enumeration.</div>
          </div>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}

function ResultCard({ num, ship }) {
  const [route, setRoute] = React.useState(null);
  const [routeLoading, setRouteLoading] = React.useState(true);

  React.useEffect(() => {
    if (!ship) { setRouteLoading(false); return; }
    let alive = true;
    setRouteLoading(true);
    api.get(`/v1/track/${num}/route`).then(({ data }) => {
      if (alive) { setRoute(data); setRouteLoading(false); }
    }).catch(() => { if (alive) setRouteLoading(false); });
    return () => { alive = false; };
  }, [num, ship]);

  if (!ship) {
    return (
      <div className="vtx-card p-6 border-l-4 border-l-[#d41f26]" data-testid={`track-notfound-${num}`}>
        <div className="font-mono text-sm text-[#0E2F76] font-semibold">{num}</div>
        <div className="text-[#d41f26] font-semibold mt-2">No shipment found for this tracking number.</div>
      </div>
    );
  }
  const pct = ship.progress_pct || 0;
  return (
    <div className="vtx-card overflow-hidden" data-testid={`track-result-${num}`}>
      <div className="p-6 md:p-8 border-b border-[#A9C0E0]/40">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 text-[#475569] text-sm">
              <ModeIcon mode={ship.mode} showLabel />
              <span>·</span>
              <span className="uppercase tracking-widest text-[11px] font-bold">{ship.service_level || "standard"}</span>
            </div>
            <div className="font-mono text-2xl md:text-3xl text-[#0E2F76] font-bold mt-2">{num}</div>
            <div className="mt-2 text-sm text-[#0f172a]">
              <span className="font-semibold">{ship.origin}</span> <span className="text-[#A9C0E0]">→</span> <span className="font-semibold">{ship.destination}</span>
            </div>
          </div>
          <div className="text-right">
            <StatusChip status={ship.status} />
            <div className="mt-3 text-[10px] tracking-widest uppercase font-bold text-[#475569]">Est. delivery</div>
            <div className="font-mono text-lg font-bold text-[#0E2F76]">{formatDateShort(ship.eta)}</div>
          </div>
        </div>

        <div className="mt-6">
          <div className="h-1.5 w-full bg-[#F4FEFF] border border-[#A9C0E0]/40 rounded-sm overflow-hidden">
            <div className="h-full bg-[#0E2F76]" style={{ width: `${pct}%` }} data-testid={`progress-${num}`} />
          </div>
          <div className="mt-2 flex justify-between text-[10px] tracking-widest uppercase font-bold text-[#475569]">
            <span>Booked</span><span>In transit</span><span>Delivered</span>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-0">
        <div className="p-6 md:p-8 md:col-span-2 border-r border-[#A9C0E0]/40">
          <div className="flex items-center justify-between mb-6">
            <div className="vtx-eyebrow">Milestones</div>
            <button onClick={()=>window.print()} className="text-xs text-[#0E2F76] hover:text-[#d41f26] inline-flex items-center gap-1.5 no-print" data-testid={`print-${num}`}>
              <Printer size={12}/> Printable view
            </button>
          </div>
          <TrackingTimeline events={ship.events || []} currentStatus={ship.status} legs={ship.legs || []} />
        </div>
        <div className="p-6 md:p-8 bg-[#F4FEFF]/50">
          <div className="vtx-eyebrow">Current location</div>
          <div className="mt-3 text-[#0E2F76] font-bold">{ship.current_location || "—"}</div>
          <div className="mt-4">
            <TrackingMap route={route} loading={routeLoading} height={220} />
            {route?.ais && (
              <div className="mt-2 text-[10px] uppercase tracking-widest font-bold text-[#d41f26]">Live AIS · {route.ais.provider}</div>
            )}
          </div>
          <div className="mt-6 space-y-3 text-sm">
            <div>
              <div className="vtx-eyebrow">Origin</div>
              <div className="mt-1">{ship.origin}</div>
            </div>
            <div>
              <div className="vtx-eyebrow">Destination</div>
              <div className="mt-1">{ship.destination}</div>
            </div>
            <div>
              <div className="vtx-eyebrow">Booked</div>
              <div className="mt-1 font-mono">{formatDate(ship.created_at)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SkeletonResults() {
  return (
    <div className="space-y-6">
      {[1,2].map(i => (
        <div key={i} className="vtx-card p-6">
          <div className="vtx-skeleton h-6 w-48 mb-3" />
          <div className="vtx-skeleton h-4 w-72 mb-6" />
          <div className="vtx-skeleton h-1.5 w-full" />
          <div className="mt-6 space-y-3">
            <div className="vtx-skeleton h-4 w-full" />
            <div className="vtx-skeleton h-4 w-3/4" />
            <div className="vtx-skeleton h-4 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}
